// Home.jsx
import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import Hero from '../components/Hero';
import PillarCard from '../components/PillarCard';
import AgConvergePreview from '../components/AgConvergePreview';
import AgConvergePopup from '../components/AgConvergePopup';
import CommunitySpotlight from '../components/CommunitySpotlight';
import '../styles/AgConvergePopup.css';
import '../styles/CommunitySpotlight.css';
import SpurLogo from '../images/SpurLogo.png';
import UNLLogo from '../images/UNLLogo.png';
import { FaLinkedin, FaInstagram } from 'react-icons/fa';

const Home = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    query: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const pillars = [
    {
      icon: '📚',
      title: 'Entrepreneurship Education',
      description:
        'First-of-its-kind curriculum combining agricultural and biosystems engineering with entrepreneurship, co-created with growers and industry leaders.',
    },
    {
      icon: '🚀',
      title: 'Startup Support Programs',
      description:
        'End-to-end support for startups including rigorous scientific and on-farm trials, product validation, mentorship, access to industry and investor networks.',
    },
    {
      icon: '🤝',
      title: 'Mentorship & Ecosystem',
      description:
        'Startups are guided by the Spur growers board and experts on product development, market entry, and fundraising. The Spur events connect innovators, farmers, researchers, and funders to drive ag innovation locally and globally.',
    },
    {
      icon: '🏭',
      title: 'Industry Engagement',
      description:
        'Strong connections with leading agricultural and food companies, fostering R&D collaboration, student internship pipeline and workforce development initiatives.',
    },
  ];

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previousData) => ({
      ...previousData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const {
        REACT_APP_EMAILJS_SERVICE_ID,
        REACT_APP_EMAILJS_TEMPLATE_ID,
        REACT_APP_EMAILJS_PUBLIC_KEY,
      } = process.env;

      if (
        !REACT_APP_EMAILJS_SERVICE_ID ||
        !REACT_APP_EMAILJS_TEMPLATE_ID ||
        !REACT_APP_EMAILJS_PUBLIC_KEY
      ) {
        throw new Error('Missing EmailJS environment variables.');
      }

      const templateParams = {
        name: `${formData.firstName} ${formData.lastName}`,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        reply_to: formData.email,
        subject: 'New Contact Form Submission',
        message: formData.query,
        to_name: 'The Spur Team',
      };

      await emailjs.send(
        REACT_APP_EMAILJS_SERVICE_ID,
        REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        REACT_APP_EMAILJS_PUBLIC_KEY
      );

      setSubmitStatus('success');

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        query: '',
      });
    } catch (error) {
      console.error('EmailJS error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="home-page">
      <AgConvergePopup />

      <div id="home">
        <Hero />
      </div>

      <div id="events">
        <AgConvergePreview />
      </div>

      <CommunitySpotlight />

      <section id="about" className="about-section">
        <div className="container">
          <h2 className="section-title">Our Innovation Ecosystem</h2>

          <p className="section-subtitle">
            The Spur Ventures supports entrepreneurs, students, researchers,
            and startup founders by providing structured curriculum, startup
            incubation, field validation, and industry engagement under one
            cohesive framework.
          </p>
        </div>
      </section>

      <section id="programs" className="pillars-section">
        <div className="container">
          <h2 className="section-title">Our Four Pillars</h2>

          <div className="pillars-grid">
            {pillars.map((pillar) => (
              <PillarCard
                key={pillar.title}
                icon={pillar.icon}
                title={pillar.title}
                description={pillar.description}
              />
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="contact-section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info">
              <h1>Contact Us</h1>

              <div className="contact-section-info">
                <h3>Follow us on</h3>

                <div className="social-links">
                  <a
                    href="https://www.linkedin.com/company/the-spur-ventures"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="The Spur Ventures on LinkedIn"
                  >
                    <FaLinkedin size={32} color="#0077B5" />
                  </a>

                  <a
                    href="https://www.instagram.com/thespurventures"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="The Spur Ventures on Instagram"
                  >
                    <FaInstagram size={32} color="#E4405F" />
                  </a>
                </div>
              </div>

              <div
                className="logos-section"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  alignItems: 'center',
                  marginTop: '30px',
                }}
              >
                <div className="spur-logo">
                  <img
                    src={SpurLogo}
                    alt="The Spur"
                    className="spur-logo-image"
                  />
                </div>

                <div className="unl-logo">
                  <img
                    src={UNLLogo}
                    alt="University of Nebraska-Lincoln Biological Systems Engineering"
                    className="unl-logo-image"
                  />
                </div>

                <p
                  className="support-text"
                  style={{
                    fontSize: '1rem',
                    color: '#e8f5e8',
                    maxWidth: '400px',
                    lineHeight: '1.5',
                    textAlign: 'center',
                    marginTop: '20px',
                  }}
                >
                  Help us drive innovation in agricultural technology and
                  support the next generation of agtech entrepreneurs.
                </p>

                <a
                  href="https://secure.nufoundation.org/give?fundid=01172520"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-cta-secondary"
                  style={{
                    display: 'inline-block',
                    marginTop: '10px',
                  }}
                >
                  Support Us
                </a>
              </div>
            </div>

            <div className="contact-form-section">
              <h3>Contact form</h3>

              {submitStatus === 'success' && (
                <div className="success-message" role="status">
                  Thank you for your message! We&apos;ll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="error-message" role="alert">
                  Sorry, there was an error sending your message. Please try
                  again.
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="email">Email</label>

                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    autoComplete="email"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>

                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    autoComplete="given-name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>

                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    autoComplete="family-name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="query">Query</label>

                  <textarea
                    id="query"
                    name="query"
                    rows={5}
                    placeholder="Tell us about your project..."
                    value={formData.query}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;